import {
  createColumnHelper,
  type AccessorFnColumnDef,
  type RowData,
} from '@tanstack/react-table';
import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useCallback,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type Ref,
  type JSX,
  Fragment,
} from 'react';
import { useIntl } from 'react-intl';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import { Button } from '../../../../buttons/button/Button.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { isSlot } from '../../../../core/utils/is-slot.js';
import { Menu, type MenuProps } from '../../../../navigation/menu/Menu.js';
import { isTrigger as isMenuTrigger } from '../../../../navigation/menu/Trigger.js';
import { BuiltinColumnHeaderName } from '../../components/BuiltinColumnHeaderName.js';
import { DataTableDefaultCell } from '../../components/DataTableDefaultCell.js';
import { DATA_TABLE_ROW_ACTIONS_COLUMN_ID } from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import * as styles from './RowActions.sty.js';
import { useCloseMenuOnScroll } from './useCloseMenuOnScroll.js';
import { useRestoreFocusOnMenuClose } from './useRestoreFocusOnMenuClose.js';

const TRIGGER_DATA_DT_CHILDREN_NAME = 'OpenRowActions';

const rowActionsColumnHelper = createColumnHelper<
  TDataTableFeatures,
  RowData
>();

/**
 * Determines the trigger element for the row actions menu. If a custom trigger is provided as a child of the Menu component, it will be used as the trigger. Otherwise, a default trigger (dot menu icon button) will be returned.
 * @param menu - the Menu component for which to determine the trigger element.
 * @param size - the size of the default trigger button, which is determined by the row density. Condensed rows get a smaller button to match the more compact styling.
 * @param ariaLabel - the aria-label for the default trigger button, which should be provided to ensure accessibility since the button only contains an icon with no visible text.
 * @returns the custom menu trigger if present, or a default menu trigger (dot menu icon button) otherwise.
 */
function getMenuTrigger(
  menu: ReactElement<MenuProps>,
  size?: 'default' | 'condensed',
  ariaLabel?: string,
) {
  const menuChildren = Children.toArray(menu.props.children) as ReactElement[];
  const customMenuTrigger = menuChildren.find(isMenuTrigger);
  if (customMenuTrigger) {
    return customMenuTrigger.props.children;
  }
  return getDefaultMenuTrigger(size, ariaLabel);
}

/**
 * Returns the default menu trigger element (dot menu icon button) for the row actions column.
 * @param size - the size of the button, which is determined by the row density. Condensed rows get a smaller button to match the more compact styling.
 * @param ariaLabel - the aria-label for the button, which should be provided to ensure accessibility since the button only contains an icon with no visible text.
 * @returns The default menu trigger element.
 */
function getDefaultMenuTrigger(
  size?: 'default' | 'condensed',
  ariaLabel?: string,
): JSX.Element {
  return (
    <Button
      variant="default"
      size={size}
      aria-label={ariaLabel}
      aria-expanded={false}
      data-dt-children-name={TRIGGER_DATA_DT_CHILDREN_NAME}
    >
      <Button.Prefix>
        <DotMenuIcon />
      </Button.Prefix>
    </Button>
  );
}
/**
 * Typeguard function that checks if a ReactElement is a Fragment,
 * which is used to determine how to extract the children for the row actions menu.
 * @param child - the ReactElement to check.
 * @returns true if the element is a Fragment, false otherwise.
 */
function isFragment(
  child: ReactElement,
): child is ReactElement<PropsWithChildren> {
  return child.type === Fragment;
}

/**
 * Internal wrapper component for the row actions children.
 * @internal
 */
const DataTableInternalRowActions = memo(
  (props: PropsWithChildren<{ rowId: string }>) => {
    const table = useDataTable();
    const intl = useIntl();
    const density =
      table.atoms.visualVariantConfig?.get().rowDensity === 'condensed'
        ? 'condensed'
        : 'default';
    const ariaLabel = intl.formatMessage(dataTableMessages.rowActionsTrigger);

    const [open, setOpen] = useState(false);

    const closeMenu = useCallback(() => {
      setOpen(false);
      table.setOpenMenuRowId(null);
    }, [table]);

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        if (!nextOpen) {
          closeMenu();
          return;
        }
        setOpen(true);
        table.setOpenMenuRowId(props.rowId);
      },
      [closeMenu, props.rowId, table],
    );

    useCloseMenuOnScroll({
      isMenuOpen: open && table.getOpenMenuRowId() === props.rowId,
      closeMenu,
      rowId: props.rowId,
      columnId: DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
    });

    const { menu, freeformSlot } = useMemo(() => {
      const slots: {
        menu: ReactElement<MenuProps> | null;
        freeformSlot: ReactNode[];
      } = {
        menu: null,
        freeformSlot: [],
      };

      const childrenArray = Children.toArray(props.children);
      for (const child of childrenArray) {
        if (isValidElement<MenuProps>(child) && child.type === Menu) {
          slots.menu = child;
          continue;
        }
        slots.freeformSlot.push(child);
      }
      return slots;
    }, [props.children]);

    const { triggerRef, onCloseAutoFocus } =
      useRestoreFocusOnMenuClose<HTMLElement>(open);

    const trigger = menu ? getMenuTrigger(menu, density, ariaLabel) : null;
    const triggerElement = isValidElement<{ ref?: Ref<HTMLElement> }>(trigger)
      ? trigger
      : null;
    // A consumer provided trigger may have its own ref defined, so we merge it with the triggerRef.
    const mergedTriggerRef = useMergeRefs<HTMLElement>([
      triggerElement?.props.ref,
      triggerRef,
    ]);

    // The menu content is consumer owned, so the focus restore handler has to be
    // cloned into it instead of passed as a prop.
    const menuContent = useMemo(
      () =>
        Children.map(menu?.props.children, (child) => {
          if (!isSlot(child, Menu.Content)) {
            return child;
          }

          const consumerOnCloseAutoFocus = child.props.onCloseAutoFocus;
          return cloneElement(child, {
            onCloseAutoFocus: (event: Event) => {
              consumerOnCloseAutoFocus?.(event);
              if (!event.defaultPrevented) {
                onCloseAutoFocus(event);
              }
            },
          });
        }),
      [menu, onCloseAutoFocus],
    );

    if (!menu) {
      return freeformSlot;
    }

    // Avoid spawning the menu while it is closed to avoid react render
    // performance issues.
    if (open === false) {
      return (
        <>
          {freeformSlot}
          <span
            onClick={() => handleOpenChange(true)}
            style={{ display: 'contents' }}
          >
            {triggerElement === null
              ? trigger
              : cloneElement(triggerElement, { ref: mergedTriggerRef })}
          </span>
        </>
      );
    }

    return (
      <>
        {freeformSlot}
        <Menu
          open={open}
          onOpenChange={handleOpenChange}
          modal={false}
          {...menu.props}
        >
          <Menu.Trigger>
            <Button
              variant="default"
              size={density}
              aria-label={ariaLabel}
              data-dt-children-name={TRIGGER_DATA_DT_CHILDREN_NAME}
            >
              <Button.Prefix>
                <DotMenuIcon />
              </Button.Prefix>
            </Button>
          </Menu.Trigger>
          {menuContent}
        </Menu>
      </>
    );
  },
);

/**
 * Column definition for the Action Column
 * @internal
 */
export const ROW_ACTIONS_COLUMN: AccessorFnColumnDef<
  TDataTableFeatures,
  RowData,
  string
> = rowActionsColumnHelper.accessor(() => '', {
  id: DATA_TABLE_ROW_ACTIONS_COLUMN_ID,
  header: () => (
    <BuiltinColumnHeaderName message={dataTableMessages.rowActionsColumnName} />
  ),
  width: 'content',
  enableSorting: false,
  enableUserActions: false,
  enableHiding: false,
  enableResizing: false,
  meta: {
    builtin: true,
    builtinPlacement: 'suffix',
    hasCustomCellRenderer: true,
    hasCustomHeaderRenderer: true,
  },
  cell: ({ row, table }) => {
    if (table.options.rowActions === undefined) {
      return null;
    }
    const actions = table.options.rowActions(row.original, {
      rowDensity: table.atoms.visualVariantConfig?.get().rowDensity,
      rowIndex: row.index,
      rowId: row.id,
    });

    if (isValidElement(actions)) {
      return (
        <DataTableDefaultCell className={styles.rowActions}>
          <DataTableInternalRowActions rowId={row.id}>
            {isFragment(actions) ? actions.props.children : actions}
          </DataTableInternalRowActions>
        </DataTableDefaultCell>
      );
    }
  },
});
